/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useClientConfig } from "services/blob/get-client-config";
import { Close, ZoomIn } from "@material-ui/icons";
import {
  Box,
  Typography,
  Dialog,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  styled,
  Modal,
  Button,
} from "@material-ui/core";

import step1 from "./images/step1.png";
import step2 from "./images/step2.png";
import step3 from "./images/step3.png";

const StyledStepContent = styled(Box)({
  minHeight: "300px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  padding: "20px",
});

const ImageContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  display: "inline-block",
  cursor: "zoom-in",
  "&:hover .overlay": {
    opacity: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)", 
  },
}));

const ImageOverlay = styled(Box)({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  opacity: 0,
  transition: "opacity 0.3s ease",
  pointerEvents: "none",
  borderRadius: "8px",
});

const StepImage = styled("img")({
  maxHeight: "200px",
  maxWidth: "100%",
  borderRadius: "8px",
  cursor: "pointer",
  display: "block",
});

const ExpandedImage = styled("img")({
  maxHeight: "90vh",
  maxWidth: "90vw",
  borderRadius: "8px",
});

const ExpandedImageContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  outline: "none",
});

export interface DownloadUverifyExtensionModalProps {
  open: boolean;
  onClose: () => void;
}

function DownloadUverifyExtensionModal({
  open,
  onClose,
}: DownloadUverifyExtensionModalProps) {
  const { t } = useTranslation("navbar-links");
  const { data: clientConfig } = useClientConfig();
  const [activeStep, setActiveStep] = useState(0);
  const [expandedImage, setExpandedImage] = useState<string | null>(null);

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    } else {
      window.open(clientConfig?.browserExtensionConfig?.firefox, "_blank");
      onClose();
    }
  };

  const handleBack = () => setActiveStep(activeStep - 1);

  const handleImageClick = (imageSrc: string) => {
    setExpandedImage(imageSrc);
  };

  const handleCloseExpandedImage = () => {
    setExpandedImage(null);
  };

  const steps = [
    {
      title: t("extension-step1"),
      content: (
        <>
          <ImageContainer onClick={() => handleImageClick(step1)}>
            <StepImage src={step1} alt="Select extensions icon" />
            <ImageOverlay className="overlay">
              <ZoomIn style={{ 
                color: "white", 
                fontSize: "2.5rem",
                filter: "drop-shadow(0 0 4px rgba(0, 0, 0, 0.8))"
              }} />
            </ImageOverlay>
          </ImageContainer>
          <Typography
            variant="body1"
            style={{ fontWeight: "bold", marginTop: "20px" }}
            gutterBottom
          >
            {t("extension-step1")}
          </Typography>
          <Typography
            variant="body1"
            style={{ color: "#555", width: "80%" }}
            gutterBottom
          >
            {t("extension-step1-text")}
          </Typography>
        </>
      ),
    },
    {
      title: t("extension-step2"),
      content: (
        <>
          <ImageContainer onClick={() => handleImageClick(step2)}>
            <StepImage src={step2} alt="Add extension to Firefox" />
            <ImageOverlay className="overlay">
              <ZoomIn style={{ 
                color: "white", 
                fontSize: "2.5rem",
                filter: "drop-shadow(0 0 4px rgba(0, 0, 0, 0.8))"
              }} />
            </ImageOverlay>
          </ImageContainer>
          <Typography
            variant="body1"
            style={{ fontWeight: "bold", marginTop: "20px" }}
            gutterBottom
          >
            {t("extension-step2")}
          </Typography>
          <Typography
            variant="body1"
            style={{ color: "#555", width: "80%" }}
            gutterBottom
          >
            {t("extension-step2-text")}
          </Typography>
        </>
      ),
    },
    {
      title: t("extension-step3"),
      content: (
        <>
          <ImageContainer onClick={() => handleImageClick(step3)}>
            <StepImage src={step3} alt="Extension added to Firefox" />
            <ImageOverlay className="overlay">
              <ZoomIn style={{ 
                color: "white", 
                fontSize: "2.5rem",
                filter: "drop-shadow(0 0 4px rgba(0, 0, 0, 0.8))"
              }} />
            </ImageOverlay>
          </ImageContainer>
          <Typography
            variant="body1"
            style={{ fontWeight: "bold", marginTop: "20px" }}
            gutterBottom
          >
            {t("extension-step3")}
          </Typography>
          <Typography
            variant="body1"
            style={{ color: "#555", width: "80%" }}
            gutterBottom
          >
            {t("extension-step3-text")}
          </Typography>
        </>
      ),
    },
  ];

  return (
    <>
      <Dialog
        open={open}
        fullWidth
        onClose={onClose}
        PaperProps={{
          style: {
            borderRadius: "12px",
          },
        }}
      >
        <Box
          style={{ padding: "20px" }}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6">
            {t("uverify-extension-installation")}
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>

        <Stepper
          style={{ width: "100%" }}
          activeStep={activeStep}
          alternativeLabel
        >
          {steps.map((step) => (
            <Step
              style={{ width: "20px", textAlign: "center" }}
              key={step.title}
            >
              <StepLabel>{step.title}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <StyledStepContent>{steps[activeStep].content}</StyledStepContent>

        <Box
          width="100%"
          display="flex"
          justifyContent="space-between"
          style={{ padding: "20px" }}
        >
          {activeStep > 0 ? (
            <Button variant="outlined" color="primary" onClick={handleBack}>
              {t("back")}
            </Button>
          ) : (
            <div />
          )}

          <Button variant="contained" color="primary" onClick={handleNext}>
            {activeStep === steps.length - 1
              ? t("download-and-install")
              : t("next")}
          </Button>
        </Box>
      </Dialog>

      <Modal
        open={!!expandedImage}
        onClose={handleCloseExpandedImage}
        aria-labelledby="expanded-image-modal"
        aria-describedby="expanded-image-description"
      >
        <ExpandedImageContainer>
          {expandedImage && (
            <>
              <ExpandedImage
                src={expandedImage}
                style={{ minWidth: "36%", minHeight: "36%" }}
                alt="Expanded view"
              />
              <IconButton
                style={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  color: "white",
                  backgroundColor: "rgba(0,0,0,0.5)",
                }}
                onClick={handleCloseExpandedImage}
              >
                <Close />
              </IconButton>
            </>
          )}
        </ExpandedImageContainer>
      </Modal>
    </>
  );
}

export default DownloadUverifyExtensionModal;